import User from "../../model/User";
import Model from "../../model/Model";
import IModelItem from "../../model/IModelItem";
import React, {useEffect, useState} from "react";
import {useResources} from "../../stores/ResourceProvider";
import Person from "../../model/Person";
import Plan from "../../model/Plan";
import Asset from "../../model/Asset";
import Income from "../../model/Income";
import Liability from "../../model/Liability";
import Deduction from "../../model/Deduction";
import Tax from "../../model/Tax";
import Expense from "../../model/Expense";
import Snapshot from "../../model/Snapshot";
import {DataGrid, GridColDef, GridRenderCellParams, GridToolbarQuickFilter, useGridApiRef} from "@mui/x-data-grid";
import {isoToLocalDateString} from "../../stores/StoreUtilities";
import {Box} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Dialog from "@mui/material/Dialog";

const UserActivityDialog = ({
  open,
  user,
  onClose
} : {
  open?: boolean
  user: User,
  onClose(): void
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [modelMap, setModelMap] = useState<Map<string, Model>>(new Map<string, Model>([]))
  const [modelItems, setModelItems] = useState<IModelItem[]>([])

  const {modelStore} = useResources()
  const apiRef = useGridApiRef()

  useEffect(() => {
    loadActivity(user)
  }, [user])

  const loadActivity = async (user: User) => {
    setIsLoading(true)
    const mods = await modelStore.loadUserModels(user)
    const modMap = new Map<string, Model>([])
    const items: IModelItem[] = []

    for(let m of mods) {
      const model = await modelStore.getModel(m.id)
      if (model) {
        modMap.set(model.id, model)
        model.persons.forEach((p: Person) => items.push(p))
        model.plans.forEach((p: Plan) => items.push(p))
        model.snapshots.forEach((s: Snapshot) => items.push(s))
        model.assets.forEach((a: Asset) => items.push(a))
        model.liabilities.forEach((l: Liability) => items.push(l))
        model.incomes.forEach((i: Income) => items.push(i))
        model.deductions.forEach((d: Deduction) => items.push(d))
        model.taxes.forEach((t: Tax) => items.push(t))
        model.expenses.forEach((e: Expense) => items.push(e))
      }
    }

    items.sort((a: IModelItem, b: IModelItem) => a.updatedAt.localeCompare(b.updatedAt))
    setModelMap(modMap)
    setModelItems(items)
    setIsLoading(false)
  }

  const columns : GridColDef[] = [
    {field: 'updatedAt', headerName: 'Updated At', width: 180,
      valueFormatter: (value: string)  => isoToLocalDateString(value, "MM/dd/yyyy h:mm a")},
    {field: 'modelId', headerName: 'Model', width: 300,
      valueFormatter: (value: string) => {
        const model = modelMap.get(value)
        if (model) {
          return model.name
        } else {
          return ""
        }
    }},
    {field: 'typeName', headerName: 'Item', width: 100,
      renderCell: (params: GridRenderCellParams<any, string | unknown>) => {
        const item = params.row as IModelItem
        if (item) {
          return <span>{item.getTypename()}</span>
        } else {
          return <span></span>
        }
      }},
    {field: 'description', headerName: 'Description', width: 300,
      valueFormatter: (value: any) => value ?? ""},
    {field: 'createdAt', headerName: 'Created At', width: 180,
      valueFormatter: (value: string)  => isoToLocalDateString(value, "MM/dd/yyyy h:mm a")},
  ]

  const QuickSearchToolbar = () => {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          p: 0.5,
          pb: 0,
        }}
      >
        <GridToolbarQuickFilter
          debounceMs={500}
        />
      </Box>
    );
  }

  const getRowId = (row: any) => {
    return row.id;
  }

  if (!user) {
    return null
  }

  return (
    <Dialog
      id="userActivityDialog"
      open={open === true}
      onClose={onClose}
      maxWidth={'lg'}
      fullWidth
      aria-labelledby="userActivityDialogTitle"
    >
      <DialogTitle id="userActivityDialogTitle">
        User Activity - {user.fullName}
      </DialogTitle>
      <DialogContent sx={{height:"calc(100vh - 140px)", maxHeight:"calc(100vh - 140px)", mt:2}}>
        <DataGrid
          rows={modelItems}
          loading={isLoading}
          getRowId={getRowId}
          disableColumnFilter
          disableColumnSelector
          disableDensitySelector
          columns={columns}
          density="standard"
          apiRef={apiRef}
          initialState={{
            sorting: {
              sortModel: [{ field: 'updatedAt', sort: 'asc' }],
            },
          }}
          slots={{ toolbar: QuickSearchToolbar }}
          sx={{overflowX:"scroll"}}
        />
      </DialogContent>
    </Dialog>
  )
}

export default UserActivityDialog